import { ChevronDownIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useRef } from 'react';
import { cn } from '@/shared/lib/utils';
import { Popover } from '@/shared/ui/Popover';
import { PopoverContent } from '@/shared/ui/Popover/components/PopoverContent';
import { PopoverRoot } from '@/shared/ui/Popover/components/PopoverRoot';
import { PopoverTrigger } from '@/shared/ui/Popover/components/PopoverTrigger';
import { Tag } from '@/shared/ui/tag/Tag';
import { type FormFieldSize, formFieldSizeVariants } from '../../helpers/field-size';

import { selectTriggerStyles } from '../helpers/trigger-styles';
import type { SelectOption } from '../helpers/types';
import { normalizeOptionValue } from '../helpers/utils';
import { MultipleSelectPopoverContent } from './MultipleSelectPopoverContent';
import { MultipleSelectTriggerContent } from './MultipleSelectTriggerContent';

type MultipleSelectProps = {
  /** フィルタリング済みオプション */
  filteredOptions: SelectOption[];
  /** 検索文字列 */
  searchValue: string;
  /** 検索文字列変更時のコールバック */
  onSearchValueChange: (value: string) => void;
  /** 選択済みの値（正規化済み） */
  selectedValues: string[];
  /** 選択済みのオプション */
  selectedOptions: SelectOption[];
  /** 値のトグル処理 */
  toggleValue: (optionValue: string | number) => void;
  /** Popover の開閉状態 */
  multiOpen: boolean;
  /** Popover 開閉時のコールバック */
  onMultiOpenChange: (open: boolean) => void;
  /** クリア処理 */
  onClear: (e: React.MouseEvent<HTMLButtonElement>) => void;
  /** 検索機能を有効にする */
  showSearch: boolean;
  /** フィールドの最小幅 */
  size: FormFieldSize;
  /** 無効化フラグ */
  disabled: boolean;
  /** ルート要素のカスタムクラス名 */
  rootClassName?: string;
  /** トリガーボタンのカスタムクラス名 */
  triggerClassName?: string;
  /** ドロップダウンコンテンツのカスタムクラス名 */
  contentClassName?: string;
  /** フィールドのID */
  id?: string;
  /** クリアボタンを表示するか */
  showAllClear: boolean;
  /** プレースホルダー */
  placeholder: string;
  /** エラー状態 */
  hasError?: boolean;
  /** キーボードナビゲーション用のアクティブインデックス */
  activeIndex: number;
  /** 検索時のキーボードイベントハンドラー */
  onSearchKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
};

/**
 * 複数選択モードの Select コンポーネント
 *
 * showSearch=true 時はトリガー内に inline input を配置し、直接検索できる
 */
export const MultipleSelect = ({
  filteredOptions,
  searchValue,
  onSearchValueChange,
  selectedValues,
  selectedOptions,
  toggleValue,
  multiOpen,
  onMultiOpenChange,
  onClear,
  showSearch,
  size,
  disabled,
  rootClassName,
  triggerClassName,
  contentClassName,
  id,
  showAllClear,
  placeholder,
  hasError = false,
  activeIndex,
  onSearchKeyDown,
}: MultipleSelectProps) => {
  const hasMultipleValue = selectedValues.length > 0;
  const shouldShowClearButton = showAllClear && hasMultipleValue && !disabled;
  const inputRef = useRef<HTMLInputElement>(null);

  const listboxId = id ? `${id}-listbox` : undefined;
  const activeOptionId = activeIndex >= 0 && id ? `${id}-option-${activeIndex}` : undefined;

  // showSearch 時はトリガー内に inline input を配置する combobox パターン
  // Radix Popover プリミティブを直接使用して onFocusOutside を制御
  // （トリガー内の input にフォーカスがある状態でPopoverを開いたままにする必要があるため）
  if (showSearch) {
    return (
      <div className={cn('relative w-full', rootClassName)}>
        <PopoverRoot open={multiOpen} onOpenChange={onMultiOpenChange}>
          <PopoverTrigger asChild disabled={disabled}>
            {/* biome-ignore lint/a11y/noStaticElementInteractions: クリックで内部 input にフォーカスを委譲するラッパー */}
            {/* biome-ignore lint/a11y/useKeyWithClickEvents: キーボード操作は内部 input の onKeyDown で処理 */}
            <div
              onClick={() => !disabled && inputRef.current?.focus()}
              data-disabled={disabled || undefined}
              className={cn(
                selectedValues.length === 0
                  ? selectTriggerStyles({ height: 'fixed' })
                  : selectTriggerStyles({ height: 'flexible' }),
                'cursor-text',
                formFieldSizeVariants({ size }),
                shouldShowClearButton && 'pr-10',
                triggerClassName,
              )}
            >
              <div className="flex flex-1 flex-wrap items-center gap-1">
                {selectedOptions.map((option) => (
                  <Tag
                    key={normalizeOptionValue(option.value)}
                    onRemove={() => toggleValue(option.value)}
                  >
                    {option.label}
                  </Tag>
                ))}
                <input
                  ref={inputRef}
                  type="text"
                  role="combobox"
                  aria-haspopup="listbox"
                  aria-expanded={multiOpen}
                  aria-controls={listboxId}
                  aria-activedescendant={activeOptionId}
                  aria-invalid={hasError || undefined}
                  disabled={disabled}
                  id={id}
                  autoComplete="off"
                  value={searchValue}
                  placeholder={selectedOptions.length === 0 ? placeholder : '検索...'}
                  onChange={(e) => {
                    onSearchValueChange(e.target.value);
                    if (!multiOpen) onMultiOpenChange(true);
                  }}
                  onKeyDown={onSearchKeyDown}
                  className={cn(
                    'min-w-15 flex-1 border-none bg-transparent text-sm shadow-none outline-none',
                    'placeholder:text-text-muted',
                    'p-0',
                    'focus:outline-none focus:ring-0',
                  )}
                />
              </div>
              {selectedValues.length === 0 && (
                <ChevronDownIcon className="h-4 w-4 shrink-0 opacity-50" />
              )}
            </div>
          </PopoverTrigger>
          <PopoverContent
            align="start"
            sideOffset={4}
            className={cn(
              'bg-surface-base',
              'flex w-[320px] flex-col space-y-2 p-2',
              contentClassName,
            )}
            onOpenAutoFocus={(event) => event.preventDefault()}
            onFocusOutside={(event) => event.preventDefault()}
          >
            <MultipleSelectPopoverContent
              filteredOptions={filteredOptions}
              selectedValues={selectedValues}
              toggleValue={toggleValue}
              activeIndex={activeIndex}
              id={id}
              listboxId={listboxId}
            />
          </PopoverContent>
        </PopoverRoot>
        {shouldShowClearButton && (
          <button
            type="button"
            onClick={onClear}
            className="absolute top-1/2 right-3 -translate-y-1/2 text-text-muted transition-colors hover:text-foreground"
            aria-label="選択をクリア"
            tabIndex={-1}
          >
            <XMarkIcon className="h-4 w-4" />
          </button>
        )}
      </div>
    );
  }

  // showSearch=false 時は従来のボタントリガー
  return (
    <div className={cn('relative w-full', rootClassName)}>
      <Popover
        open={multiOpen}
        onOpenChange={onMultiOpenChange}
        contentClassName={cn('flex w-[320px] flex-col space-y-2 p-2', contentClassName)}
        contentAlign="start"
        onOpenAutoFocus={(event) => event.preventDefault()}
        content={
          <MultipleSelectPopoverContent
            filteredOptions={filteredOptions}
            selectedValues={selectedValues}
            toggleValue={toggleValue}
            activeIndex={activeIndex}
            id={id}
            listboxId={listboxId}
          />
        }
      >
        <button
          type="button"
          role="combobox"
          aria-haspopup="listbox"
          aria-expanded={multiOpen}
          aria-controls={listboxId}
          disabled={disabled}
          id={id}
          aria-invalid={hasError || undefined}
          className={cn(
            selectedValues.length === 0
              ? selectTriggerStyles({ height: 'fixed' })
              : selectTriggerStyles({ height: 'flexible' }),
            formFieldSizeVariants({ size }),
            shouldShowClearButton && 'pr-10',
            triggerClassName,
          )}
        >
          <MultipleSelectTriggerContent
            selectedOptions={selectedOptions}
            placeholder={placeholder}
            toggleValue={toggleValue}
          />
          {selectedValues.length === 0 && (
            <ChevronDownIcon className="h-4 w-4 shrink-0 opacity-50" />
          )}
        </button>
      </Popover>
      {shouldShowClearButton && (
        <button
          type="button"
          onClick={onClear}
          className="absolute top-1/2 right-3 -translate-y-1/2 text-text-muted transition-colors hover:text-foreground"
          aria-label="選択をクリア"
          tabIndex={-1}
        >
          <XMarkIcon className="h-4 w-4" />
        </button>
      )}
    </div>
  );
};
